import { createPin } from "./pin.js";

/** @typedef {import("./api.js").Property} Property */

/**
 * マーカー群を操作するインターフェース
 * @typedef {Object} MarkerLayer
 * @property {(properties: Property[]) => void} update - マーカーを丸ごと更新する
 * @property {() => void} clear - すべてのマーカーを地図から外して空にする
 * @property {(target: google.maps.marker.AdvancedMarkerElement) => void} select - 指定マーカーだけを選択スタイルにする
 * @property {() => void} deselectAll - すべてのピンを通常スタイルに戻す
 */

/**
 * マーカー群の生成・クリア・選択ハイライトを管理する
 * @param {Object} params
 * @param {google.maps.Map} params.map - Google Maps 本体
 * @param {typeof google.maps.marker.AdvancedMarkerElement} params.AdvancedMarkerElement - マーカークラス（map.js の initMap が返すもの）
 * @param {(marker: google.maps.marker.AdvancedMarkerElement, property: Property) => void} params.onPinClick - ピンクリック時のコールバック
 * @returns {MarkerLayer}
 */
export const createMarkerLayer = ({ map, AdvancedMarkerElement, onPinClick }) => {
    /**
     * 現在表示中のマーカー（カテゴリ別に分けず全走査のみのためフラット配列で保持）
     * @type {google.maps.marker.AdvancedMarkerElement[]}
     */
    let markers = [];

    /**
     * marker → property の対応（Google Maps オブジェクトに expando を生やさない）
     * @type {Map<google.maps.marker.AdvancedMarkerElement, Property>}
     */
    const propertyByMarker = new Map();

    /**
     * すべてのマーカーを地図から外して空にする
     * @returns {void}
     */
    const clear = () => {
        markers.forEach((marker) => marker.setMap(null));
        markers = [];
        propertyByMarker.clear();
    };

    /**
     * 1件分のマーカーを生成する
     * @param {Property} property - 物件データ
     * @returns {google.maps.marker.AdvancedMarkerElement}
     */
    const createMarker = (property) => {
        const marker = new AdvancedMarkerElement({
            position: {
                lat: property.geopoint.lat,
                lng: property.geopoint.lng,
            },
            map,
            content: createPin(property.category_type),
        });

        propertyByMarker.set(marker, property);
        marker.addListener("gmp-click", () => onPinClick(marker, property));

        return marker;
    };

    /**
     * マーカーを丸ごと更新する
     * @param {Property[]} properties - 表示する物件データ一覧
     * @returns {void}
     */
    const update = (properties) => {
        clear();
        markers = properties.map(createMarker);
    };

    /**
     * すべてのピンを通常スタイルに戻す
     * @returns {void}
     */
    const deselectAll = () => {
        markers.forEach((marker) => {
            const property = propertyByMarker.get(marker);
            marker.content = createPin(property.category_type);
        });
    };

    /**
     * 指定マーカーだけを選択スタイルにする
     * @param {google.maps.marker.AdvancedMarkerElement} target - 選択するマーカー
     * @returns {void}
     */
    const select = (target) => {
        deselectAll();
        const property = propertyByMarker.get(target);
        if (property) {
            target.content = createPin(property.category_type, true);
        }
    };

    return { update, clear, select, deselectAll };
};
